import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { StoredCookies } from '@/interfaces/auth/cookies.enum';
import { subscriptionService, SubscriptionStatus } from '@/services/subscription.service';
import { notificationService } from '@/services/notification.service';
import { Modal } from '@/components/common/Modal';

export const Settings = () => {
  const [cookies] = useCookies([StoredCookies.USERNAME]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);

  const username = cookies[StoredCookies.USERNAME];

  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const status = await subscriptionService.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setSubscriptionStatus(SubscriptionStatus.INACTIVE);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const handleCancelSubscription = () => {
    setShowCancelSubscriptionModal(true);
  };

  const confirmCancelSubscription = async () => {
    setIsCancellingSubscription(true);
    try {
      const response = await subscriptionService.cancelSubscription();
      if (response.success && response.status === 'canceled') {
        notificationService.success('Subscription cancelled successfully');
        setSubscriptionStatus(SubscriptionStatus.CANCELED);
      } else {
        notificationService.error('Unable to cancel subscription');
      }
    } catch (error) {
      if (error instanceof Error) {
        notificationService.error(error.message);
      } else {
        notificationService.error('Error cancelling subscription');
      }
    } finally {
      setIsCancellingSubscription(false);
      setShowCancelSubscriptionModal(false);
    }
  };

  const getSubscriptionBadge = () => {
    if (isLoadingStatus) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
          Loading...
        </span>
      );
    }

    switch (subscriptionStatus) {
      case SubscriptionStatus.ACTIVE:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Active
          </span>
        );
      case SubscriptionStatus.CANCELED:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Canceled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            Inactive
          </span>
        );
    }
  };

  return (
    <>
      <Modal
        isOpen={showCancelSubscriptionModal}
        onClose={() => setShowCancelSubscriptionModal(false)}
        title="Cancel Subscription"
      >
        <div className="p-4">
          <p className="mb-4 font-body">Are you sure you want to cancel your subscription? This will end your subscription benefits.</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowCancelSubscriptionModal(false)}
              data-testid="cancel-cancellation-button"
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              disabled={isCancellingSubscription}
            >
              Keep Subscription
            </button>
            <button
              onClick={confirmCancelSubscription}
              data-testid="confirm-cancellation-button"
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              disabled={isCancellingSubscription}
            >
              {isCancellingSubscription ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  Cancelling...
                </>
              ) : (
                'Cancel Subscription'
              )}
            </button>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl font-bold text-[#0F9015] mb-8" data-testid="page-title">My Account</h1>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Account Information Section */}
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-[#084509] mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Account Information
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email / Username</label>
                  <p className="text-lg text-[#084509] font-semibold" data-testid="user-email">
                    {username || 'Not available'}
                  </p>
                </div>
              </div>
            </div>

            {/* Subscription Section */}
            <div className="p-6">
              <h2 className="text-xl font-semibold text-[#084509] mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Subscription
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                    {getSubscriptionBadge()}
                  </div>
                </div>

                {subscriptionStatus === SubscriptionStatus.ACTIVE && (
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500 mb-3">
                      Want to cancel your subscription? You can do so below.
                    </p>
                    <button
                      onClick={handleCancelSubscription}
                      data-testid="cancel-subscription-button"
                      className="px-4 py-2 bg-red-100 text-red-700 font-semibold rounded-lg hover:bg-red-200 transition-colors border border-red-300"
                    >
                      Cancel Subscription
                    </button>
                  </div>
                )}

                {subscriptionStatus === SubscriptionStatus.INACTIVE && (
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      You don't have an active subscription. Subscribe to access bulk ticket creation features.
                    </p>
                  </div>
                )}

                {subscriptionStatus === SubscriptionStatus.CANCELED && (
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      Your subscription has been canceled. You can subscribe again to regain access to premium features.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

